import axios from "axios";
import baseAxios from "./base";

// Create PRIVATE instance (cookies enabled)
const privateAxiosInstance = axios.create({
    baseURL: baseAxios.defaults.baseURL,
    withCredentials: true, // 🔑 REQUIRED for HttpOnly cookies
    timeout: 120000,
});

const getTenantSlug = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");

    // Known non-tenant subdomains
    const ignore = ["www", "dev", "test", "staging", "prod"];

    if (parts.length <= 2) return null;

    // If first part is ignored, then no tenant
    if (ignore.includes(parts[0])) return null;

    return parts[0];
};



// ======================================================
// REQUEST INTERCEPTOR → Attach X-Tenant-Slug
// ======================================================
privateAxiosInstance.interceptors.request.use(
    (config) => {
        const tenantSlug = getTenantSlug();

        if (tenantSlug) {
            config.headers["X-Tenant-Slug"] = tenantSlug;
        }

        return config;
    },
    (error) => Promise.reject(error)
);


// ===== Refresh coordination =====
let isRefreshing = false;
let isLoggingOut = false;  // guard against re-entrant logout calls
let failedQueue = [];

// Pages that don't require auth — suppress refresh/logout cycle on these
const PUBLIC_PATHS = [
    "/login",
    "/email/redirect",
    "/register",
    "/tenantRegister",
    "/forgot/password",
    "/generate-password",
    "/auth/callback",
    "/contact-us",
    "/privacy-policy",
    "/pricing-detail",
    "/interview",
    "/flowiseInterview"
];

// Also treat the root landing page as public
const isOnPublicPage = () => {
    const path = window.location.pathname;
    // Exact match for root; prefix match for all others
    if (path === "/" || path === "") return true;
    return PUBLIC_PATHS.some((p) => path.startsWith(p));
};

const processQueue = (error) => {
    failedQueue.forEach(({ resolve, reject }) => {
        error ? reject(error) : resolve();
    });
    failedQueue = [];
};


// ======================================================
// RESPONSE INTERCEPTOR → Refresh Handling
// ======================================================
privateAxiosInstance.interceptors.response.use(
    (response) => response,

    async (error) => {
        const originalRequest = error.config || {};
        const status = error.response?.status;
        const url = originalRequest.url || "";

        // 🚫 Skip refresh logic for public auth routes
        if (
            url.includes("/user/login") ||
            url.includes("/user/refresh") ||
            url.includes("/user/logout") ||
            url.includes("/user/register") ||
            url.includes("/user/forgot")
        ) {
            return Promise.reject(error);
        }

        // Skip if a logout is already in progress
        if (isLoggingOut) {
            return Promise.reject(error);
        }

        // Skip refresh/logout cycle if we're already on a public/auth page
        // (e.g. logo call from login page should not trigger a reload loop)
        if (isOnPublicPage()) {
            return Promise.reject(error);
        }

        // ✅ Treat missing response (proxy drop) as a 401 if not already retried
        const isAuthFailure = status === 401 || (!error.response && !originalRequest._retry);

        if (isAuthFailure && !originalRequest._retry) {

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => privateAxiosInstance(originalRequest));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // console.log("Calling refresh API...");
                await privateAxiosInstance.post("/user/refresh/");

                processQueue(null);
                return privateAxiosInstance(originalRequest);

            } catch (refreshError) {
                console.error("Refresh failed", refreshError);
                processQueue(refreshError);

                // Only logout if refresh itself got a real auth error
                if (refreshError.response?.status === 401) {
                    logout();
                }

                return Promise.reject(refreshError);

            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);


// ======================================================
// LOGOUT
// ======================================================
const logout = async () => {
    if (isLoggingOut) return;  // prevent re-entrant calls
    isLoggingOut = true;

    try {
        // Use privateAxiosInstance so withCredentials: true applies (cookies are sent)
        await privateAxiosInstance.post("/user/logout/");
    } catch (err) {
        console.error("Logout API failed", err);
    } finally {
        // Only redirect to /login if we're not already on a public page
        // This prevents the infinite reload loop caused by auth calls on the login page
        if (!isOnPublicPage()) {
            window.location.replace("/email/redirect");
        }
        isLoggingOut = false;
    }
};

export default privateAxiosInstance;

